import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { getBrandingConfig, getAppearanceConfig } from '../config/themeConfig';
import { useTheme } from './ThemeContext';

interface Theme {
  primary: string;
  secondary: string;
  background: string;
  text: string;
}

interface AppearanceContextType {
  theme: Theme;
  logo: string;
  appName: string;
  appType: string;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  updateTheme: (newTheme: Partial<Theme>) => void;
  updateLogo: (newLogo: string) => void;
  updateAppName: (newName: string) => void;
  resetToDefaults: () => void;
}

const STORAGE_KEY = 'finbot_appearance_settings';

const finbotTheme: Theme = {
  primary: '#3b82f6',
  secondary: '#1e40af',
  background: '#ffffff',
  text: '#202124'
};

const getDefaultBranding = () => {
  const brandingConfig = getBrandingConfig();

  return {
    logo: '/Assets/Images/logo_so_vn.png',
    name: brandingConfig.name,
    type: brandingConfig.type
  };
};

const loadStoredSettings = () => {
  const appearanceConfig = getAppearanceConfig();
  if (!appearanceConfig.enableCustomization) {
    return null;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading stored settings:', error);
  }

  return null;
};

const saveSettings = (settings: any) => {
  // Only save settings if customization is allowed using theme config
  const appearanceConfig = getAppearanceConfig();
  if (appearanceConfig.enableCustomization) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }
};

export const AppearanceContext = createContext<AppearanceContextType | undefined>(undefined);

export const AppearanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const defaultBranding = useMemo(() => getDefaultBranding(), []);
  const storedSettings = loadStoredSettings();
  const appearanceConfig = getAppearanceConfig();
  const { isDarkMode, toggleDarkMode } = useTheme();

  // Initialize with stored settings or defaults, respecting theme configuration
  const [appType] = useState<string>(appearanceConfig.enableCustomization ? storedSettings?.appType || defaultBranding.type : defaultBranding.type);

  const defaultTheme = useMemo(() => finbotTheme, []);

  const [theme, setTheme] = useState<Theme>(appearanceConfig.allowThemeChange ? storedSettings?.theme || defaultTheme : defaultTheme);

  const [logo, setLogo] = useState<string>(appearanceConfig.allowLogoUpload ? storedSettings?.logo || defaultBranding.logo : defaultBranding.logo);

  const [appName, setAppName] = useState<string>(
    appearanceConfig.allowNameChange ? storedSettings?.appName || defaultBranding.name : defaultBranding.name
  );

  // Save settings whenever they change
  useEffect(() => {
    if (appearanceConfig.enableCustomization) {
      saveSettings({
        theme,
        logo,
        appName,
        appType,
        isDarkMode,
        lastUpdated: new Date().toISOString()
      });
    }

    // Update CSS variables
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primary);
    root.style.setProperty('--secondary-color', theme.secondary);
    root.style.setProperty('--background-color', theme.background);
    root.style.setProperty('--text-color', theme.text);

    // Update document title
    document.title = appName;
  }, [theme, logo, appName, appType, isDarkMode, appearanceConfig.enableCustomization]);

  const updateTheme = useCallback(
    (newTheme: Partial<Theme>) => {
      if (appearanceConfig.allowThemeChange) {
        setTheme((prev) => ({ ...prev, ...newTheme }));
      }
    },
    [appearanceConfig.allowThemeChange]
  );

  const updateLogo = useCallback(
    (newLogo: string) => {
      if (appearanceConfig.allowLogoUpload) {
        setLogo(newLogo);
      }
    },
    [appearanceConfig.allowLogoUpload]
  );

  const updateAppName = useCallback(
    (newName: string) => {
      if (appearanceConfig.allowNameChange) {
        setAppName(newName);
      }
    },
    [appearanceConfig.allowNameChange]
  );

  const resetToDefaults = useCallback(() => {
    if (appearanceConfig.enableCustomization) {
      const defaultBranding = getDefaultBranding();
      const defaultTheme = finbotTheme;

      setTheme(defaultTheme);
      setLogo(defaultBranding.logo);
      setAppName(defaultBranding.name);

      // Reset dark mode as well
      if (isDarkMode) {
        toggleDarkMode();
      }

      // Clear stored settings
      localStorage.removeItem(STORAGE_KEY);
      // Also remove old theme key if it exists
      localStorage.removeItem('theme');
    }
  }, [appearanceConfig.enableCustomization, isDarkMode, toggleDarkMode]);

  useEffect(() => {
    console.log('AppearanceContext initialized with:', {
      appType,
      appName,
      theme: 'Finbot Theme',
      customizationEnabled: appearanceConfig.enableCustomization,
      allowLogoUpload: appearanceConfig.allowLogoUpload,
      allowNameChange: appearanceConfig.allowNameChange,
      allowThemeChange: appearanceConfig.allowThemeChange
    });
  }, [appType, appName, appearanceConfig]);

  return (
    <AppearanceContext.Provider
      value={{
        theme,
        logo,
        appName,
        appType,
        isDarkMode,
        toggleDarkMode,
        updateTheme,
        updateLogo,
        updateAppName,
        resetToDefaults
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (context === undefined) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return context;
};
